import React, { memo, useCallback, useEffect, useState } from "react";
import * as XLSX from "xlsx";

import { ConfirmDeletePopup } from "../../components/confirm-delete-popup";
import ImportButton from "../../components/import-button";
import { TableFilterInputText } from "../../components/table-filter-inputs";
import { TableFilterInputSelect } from "../../components/table-filter-inputs/select";
import { Button, CreateAlert, Icon, ITableStructureItem } from "../../modules";
import { TableSimplePagination } from "../../modules/table/TableSimplePagination";
import { CategoryService } from "../../services/category";
import { SingerService } from "../../services/singer";
import { SongService } from "../../services/song";
import "./index.scss";
import { LanguageService } from "../../services/language/language.service";

const SongList = () => {
  const [filterParams, setFilterParams] = useState<any>({
    page: 1,
    pageSize: 10,
  });
  const [loading, setLoading] = useState(false);
  const [dataList, setDataList] = useState<any>();
  const [languageList, setLanguageList] = useState<{ label: string, value: string | number }[]>([]);
  const [catList, setCatList] = useState<{ label: string, value: string | number }[]>([]);
  const [singerList, setSingerList] = useState<{ label: string, value: string | number }[]>([]);
  const [activeDelete, setActiveDelete] = useState<{ open: boolean, data: any }>({
    open: false,
    data: undefined,
  });

  const handleExportExcel = async () => {
    return new Promise(async (resolve) => {
      try {
        const response = await SongService.getList({
          ...filterParams,
          page: 1,
          pageSize: 1000000,
        });

        const data = response.data;

        const fileHead = structure.map((v) => v.name);
        const dataExport = [
          fileHead,
          ...data.map((item: any) =>
            structure.map((column) => {
              if (!column.key) return "";
              return item[column.key] === true ? 1 : item[column.key] === false ? 0 : item[column.key];
            })
          ),
        ];

        const ws = XLSX.utils.aoa_to_sheet(dataExport);
        const wb = XLSX.utils.book_new();
        XLSX.utils.book_append_sheet(wb, ws, "SheetJS");

        const now = new Date();
        XLSX.writeFile(
          wb,
          `Danh sach bai hat ${now.toLocaleDateString().replace(/\//g, "-")} ${now
            .toLocaleTimeString()
            .replace(/:/g, "-")}.xlsx`,
          { type: "binary" }
        );

        resolve(
          CreateAlert({
            type: "success",
            message: "Export data success.",
          })
        );
      } catch (error: any) {
        resolve(CreateAlert({ type: "danger", message: error.message }));
      }
    });
  };

  const handleImport = async (form: FormData ,cb: any) => {
    try {
     const res: any = await SongService.import(form)
 
     if (res?.success) {
       const _params = { page: 1, pageSize: 10 }
       setFilterParams({..._params})
       cb()
       CreateAlert({
         type: "success",
         message: "Nhập dữ liệu thành công",
       })
     }
 
    } catch (error: any) {
       cb()
       CreateAlert({
         type: "danger",
         message: error.message || 'Xóa dữ liệu thất bại',
       })
    }
   }

  const handleDelete = async () => {
    if (!activeDelete?.data) return

    try {
      const res = await SongService.delete(activeDelete?.data?.songId)

      if (res?.success) {
        setActiveDelete({ open: false, data: undefined })
        const _params = { page: 1, pageSize: 10 }
        setFilterParams({..._params})
        CreateAlert({
          type: "success",
          message: "Xóa dữ liệu thành công",
        })
      }
    } catch (error: any) {
      setActiveDelete({ open: false, data: undefined })
      CreateAlert({
        type: "danger",
        message: error.message || 'Xóa dữ liệu thất bại',
      })
    }
  }

  const fetchData = useCallback(async () => {
    setLoading(true);
    const data = await SongService.getList(filterParams);
    setDataList(data);
    setLoading(false);
  }, [filterParams]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  // Table Data
  const structure: ITableStructureItem[] = [
    {
      name: "STT",
      key: "idx",
    },
    {
      name: "ID",
      key: "songId",
    },
    {
      name: "Tên đầy đủ",
      key: "fullName",
    },
    {
      name: "Mô tả",
      key: "bonusData",
      render: (item) => (item?.bonusData || '--'),
    },
    {
      name: "Tên tìm kiếm",
      key: "nameForSearch",
    },
    {
      name: "Tên tìm kiếm (short)",
      key: "shortName",
    },
    {
      name: "Tên file thực tế",
      key: "fileName",
      render: (item) => (item?.fileName || '--'),
    },
    {
      name: "Mã ca sĩ",
      key: "singerId",
    },
    {
      name: "Tên ca sĩ",
      key: "singerName",
    },
    {
      name: "Thể loại",
      key: "genreName",
    },
    {
      name: "Song ca/Đơn ca",
      key: "isDuet",
      render: (item) => (item?.isDuet ? "Song ca" : "Đơn ca"),
    },
    {
      name: "Trạng thái",
      key: "isActive",
      render: (item) => (item?.isActive ? "Kích hoạt" : "Khóa"),
    },
    {
      name: "Download file",
      key: "trackUrl",
      render: (item) =>
        !item?.trackUrl ? (
          "--"
        ) : (
          <a
            href={item?.trackUrl}
            target="_blank"
            rel="noopener noreferrer"
          >
            {item?.fileName || item?.nameForSearch}
          </a>
        ),
    },
    {
      name: "Auth",
      key: "auth",
      render: (item) => item?.auth || "--",
    },
    {
      name: "Ngôn ngữ",
      key: "language",
      render: (item: any) => item?.language || "--",
    },
    {
      name: "Thao tác",
      render: (item) => {
        return (
          <div className="btnGroup">
            <span
              className="btn btn__edit"
                onClick={() =>  setActiveDelete({
                  open: true,
                  data: item,
                })}
            >
              <Icon.Delete />
            </span>
          </div>
        );
      },
    },
  ];

  const filters = [
    {
      name: "Tìm kiếm theo id, tên đầy đủ, tên tìm kiếm, tên tìm kiếm(short)",
      key: "searchString",
      input: (s: any) => {
        return (
          <TableFilterInputText
            {...s}
            params={{
              ...s.params,
              searchString: filterParams?.searchString,
            }}
          />
        );
      },
    },
    {
      name: "Thể loại",
      key: "genreId",
      input: (s: any) => {
        return (
          <TableFilterInputSelect
            {...s}
            params={{ ...s?.params, genreId: filterParams?.genreId }}
            options={catList}
          />
        );
      },
    },
    {
      name: "Ca sĩ",
      key: "singerId",
      input: (s: any) => {
        return (
          <TableFilterInputSelect
            {...s}
            params={{ ...s?.params, singerId: filterParams?.singerId }}
            options={singerList}
          />
        );
      },
    },
    {
      name: "Song ca/Đơn ca",
      key: "isDuet",
      input: (s: any) => {
        return (
          <TableFilterInputSelect
            {...s}
            params={{ ...s?.params, isDuet: filterParams?.isDuet }}
            options={SongService.getFilterOptions().isDuet}
          />
        );
      },
    },
    {
      name: "Trạng thái",
      key: "isActive",
      input: (s: any) => {
        return (
          <TableFilterInputSelect
            {...s}
            params={{ ...s?.params, isActive: filterParams?.isActive }}
            options={SongService.getFilterOptions().isActive}
          />
        );
      },
    },
    {
      name: "Ngôn ngữ",
      key: "language",
      input: (s: any) => {
        return (
          <TableFilterInputSelect
            {...s}
            params={{ ...s?.params, language: filterParams?.language }}
            options={languageList}
          />
        );
      },
    },
    {
      name: "Làm mới",
      input: () => (
        <Button
          label="Xác nhận"
          buttonType="info"
          onClick={() => setFilterParams({ page: 1, pageSize: 10 })}
        />
      ),
    },
  ]

  useEffect(() => {
    const fetchCategory = async () => {
      const res = await CategoryService.getList({ pageSize: 1000000 })
      if (res?.data) setCatList(res?.data?.map((x: any) => ({ label: x?.name, value: x?.genreId })))
    }

    const fetchSinger = async () => {
      const res = await SingerService.getList({ pageSize: 1000000 })
      if (res?.data) setSingerList(res?.data?.map((x: any) => ({ label: x?.name, value: x?.singerId })))
    }

    const fetchLanguage = async () => {
      const res = await LanguageService.getList({ pageSize: 1000000 })
      if (res?.data) setLanguageList(res?.data?.map((x: any) => ({ label: x, value: x })))
    }

    Promise.all([fetchCategory(), fetchSinger(), fetchLanguage()])
  }, [])

  return (
    <div className="song-list">
      <div className="title">Quản lý bài hát</div>

      <div className="content-wrapper">
        <div className="d-flex mb20" style={{ gap: 20 }}>
          <ImportButton fileUrl="/templates/Template_import-song.xls" onImport={handleImport}/>

          <Button
            label="Export excel"
            buttonType="success"
            onClick={handleExportExcel}
          />
        </div>

        <TableSimplePagination
          dataTable={{
            dataList: dataList,
            filterParams: filterParams,
            totalPage: dataList
              ? Math.ceil(dataList?.count / filterParams?.pageSize)
              : 0,
            loading,
          }}
          setParams={setFilterParams}
          filters={filters}
          structure={structure}
        />
      </div>

      {activeDelete?.open && (
        <ConfirmDeletePopup
          onClose={() => setActiveDelete({ open: false, data: undefined })}
          onConfirm={handleDelete}
        />
      )}
    </div>
  );
};

export default memo(SongList);
